import React, { FC, useCallback, useMemo } from 'react';
import { GridItem } from '../lib/components/Grid';
import useClassifications from '../hooks/useSubjects';
import SingleSelect from '../lib/components/SingleSelect';
import { SelectOption } from '../typings/select-option.typings';

export interface SelectClassificationState {
  area: SelectOption;
  expertise: SelectOption;
  topic: SelectOption;
  subtopic: SelectOption;
}

type colRange = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface SelectClassificationProps {
  disabled?: boolean;
  gridXsCols?: [colRange, colRange, colRange, colRange];
  gridSmCols?: [colRange, colRange, colRange, colRange];
  gridCols?: [colRange, colRange, colRange, colRange];
  gridLgCols?: [colRange, colRange, colRange, colRange];
  gridXlCols?: [colRange, colRange, colRange, colRange];
  gridXxlCols?: [colRange, colRange, colRange, colRange];
  values: SelectClassificationState;
  onChange: (value: SelectClassificationState) => void;
  labels?: [string, string, string, string];
}

export const selectOptionDefault: SelectOption = {
  label: '',
  value: '',
};

export const singleSelectInitialState: SelectClassificationState = {
  area: selectOptionDefault,
  expertise: selectOptionDefault,
  topic: selectOptionDefault,
  subtopic: selectOptionDefault,
};

const SelectClassification: FC<SelectClassificationProps> = ({
  disabled,
  gridXsCols,
  gridSmCols,
  gridCols = [3, 3, 3, 3],
  gridLgCols,
  gridXlCols,
  gridXxlCols,
  onChange,
  values,
  labels = ['Todas', 'Todas', 'Todas', 'Todas'],
}) => {
  const classifications = useClassifications();

  const _disabledExpertise = useMemo(() => {
    return disabled || !values.area.value;
  }, [disabled, values.area]);

  const _disabledTopic = useMemo(() => {
    return disabled || !values.expertise.value;
  }, [disabled, values.expertise]);

  const _disabledSubtopic = useMemo(() => {
    return disabled || !values.topic.value;
  }, [disabled, values.topic]);

  const handleArea = useCallback((value) => {
    if (values.area.value !== value.value) {
      onChange({
        area: value,
        expertise: selectOptionDefault,
        topic: selectOptionDefault,
        subtopic: selectOptionDefault,
      });
    }
  }, [onChange, values]);

  const handleExpertise = useCallback((value) => {
    if (values.expertise.value !== value.value) {
      onChange({
        ...values,
        expertise: value,
        topic: selectOptionDefault,
        subtopic: selectOptionDefault,
      });
    }
  }, [onChange, values]);

  const handleTopic = useCallback((value) => {
    if (values.topic.value !== value.value) {
      onChange({ ...values, topic: value, subtopic: selectOptionDefault });
    }
  }, [onChange, values]);

  const handleSubtopic = useCallback((value) => {
    if (values.subtopic.value !== value.value) {
      onChange({ ...values, subtopic: value });
    }
  }, [onChange, values]);

  const gXs = useMemo(() => gridXsCols || gridSmCols || gridCols, [gridCols, gridSmCols, gridXsCols]);
  const gSm = useMemo(() => gridSmCols || gridCols, [gridCols, gridSmCols]);
  const gLg = useMemo(() => gridLgCols || gridCols, [gridCols, gridLgCols]);
  const gXl = useMemo(() => gridXlCols || gridLgCols || gridCols, [gridCols, gridLgCols, gridXlCols]);
  const gXxl = useMemo(() => {
    return gridXxlCols || gridXlCols || gridLgCols || gridCols;
  }, [gridCols, gridLgCols, gridXlCols, gridXxlCols]);

  return (
    <>
      <GridItem
        colXs={gXs[0]}
        colSm={gSm[0]}
        col={gridCols[0]}
        colLg={gLg[0]}
        colXl={gXl[0]}
        colXxl={gXxl[0]}
      >
        <SingleSelect
          label="Área"
          name="area"
          placeholder="Área"
          onChange={handleArea}
          disabled={disabled || classifications.loading}
          value={values.area.value}
          options={[
            {
              value: '',
              label: labels[0],
            },
            ...classifications.getList().map((c) => ({
              value: c._id,
              label: c.name,
            })),
          ]}
        />
      </GridItem>

      <GridItem
        colXs={gXs[1]}
        colSm={gSm[1]}
        col={gridCols[1]}
        colLg={gLg[1]}
        colXl={gXl[1]}
        colXxl={gXxl[1]}
      >
        <SingleSelect
          label="Especialidade"
          name="expertise"
          placeholder="Especialidade"
          onChange={handleExpertise}
          disabled={_disabledExpertise || classifications.loading}
          value={values.expertise.value}
          options={[
            {
              value: '',
              label: labels[1],
            },
            ...classifications.getChildrenList(values.area.value).map((c) => ({
              value: c._id,
              label: c.name,
            })),
          ]}
        />
      </GridItem>

      <GridItem
        colXs={gXs[2]}
        colSm={gSm[2]}
        col={gridCols[2]}
        colLg={gLg[2]}
        colXl={gXl[2]}
        colXxl={gXxl[2]}
      >
        <SingleSelect
          label="Tema"
          name="topic"
          placeholder="Tema"
          onChange={handleTopic}
          disabled={_disabledTopic || classifications.loading}
          value={values.topic.value}
          options={[
            {
              value: '',
              label: labels[2],
            },
            ...classifications.getChildrenList(values.expertise.value).map((c) => ({
              value: c._id,
              label: c.name,
            })),
          ]}
        />
      </GridItem>

      <GridItem
        colXs={gXs[3]}
        colSm={gSm[3]}
        col={gridCols[3]}
        colLg={gLg[3]}
        colXl={gXl[3]}
        colXxl={gXxl[3]}
      >
        <SingleSelect
          label="Subtema"
          name="subtopic"
          placeholder="Subtema"
          onChange={handleSubtopic}
          disabled={_disabledSubtopic || classifications.loading}
          value={values.subtopic.value}
          options={[
            {
              value: '',
              label: labels[3],
            },
            ...classifications.getChildrenList(values.topic.value).map((c) => ({
              value: c._id,
              label: c.name,
            })),
          ]}
        />
      </GridItem>
    </>
  );
};

export default SelectClassification;
